import React, { useCallback, useEffect, useState } from "react";
import { BuyOptionCard } from "web/react/components/buy-option-card";
import { Button } from "web/react/emo/button";
import { RadioCardGroup } from "web/react/emo/radio-card-group";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import {
    getBuyOptionStickerType,
    getPromotion,
    getShippingInformation,
} from "web/react/pages/product/in-stock/in-stock-product-page/utils";
import analytics from "web/script/analytics/analytics";
import { gettext, ngettext } from "web/script/modules/django-i18n";
import { ProductBuyOptionsSerializer, ProductSerializer } from "web/types/serializers";
import * as styles from "./price-comparison-cards.css";

interface PriceComparisonCardsProps {
    product: ProductSerializer;
    buyOptions: ProductBuyOptionsSerializer[];
}

interface PriceComparisonCardsDeviceProps extends PriceComparisonCardsProps {
    onBuyButtonClick: (buyOption: ProductBuyOptionsSerializer, index: number) => void;
    onRetailerClick: (buyOption: ProductBuyOptionsSerializer, index: number) => void;
}

function PriceComparisonCardsMobile({
    product,
    buyOptions,
    onBuyButtonClick,
    onRetailerClick,
}: PriceComparisonCardsDeviceProps): JSX.Element {
    const {
        activeBuyOption,
        comparison: { closeCompareModal },
        productPurchaseType,
    } = useInStockProductPageContext();

    const isCheckout = productPurchaseType === ProductPurchaseType.CHECKOUT;

    const [selectedBuyOption, setSelectedBuyOption] = useState(activeBuyOption.buyOption);

    const selectBuyOption = useCallback(() => {
        analytics.event("price_comparison", "selected");
        activeBuyOption.selectBuyOption(selectedBuyOption);
        closeCompareModal();
    }, [activeBuyOption, selectedBuyOption, closeCompareModal]);

    const onItemSelect = useCallback(
        (buyOption: ProductBuyOptionsSerializer) => setSelectedBuyOption(buyOption),
        []
    );

    return (
        <form className={styles.mobileWrapper}>
            <div className={styles.mobileCardsHeading}>
                <Text as="p" textStyle={"body-s"}>
                    {gettext("price_comparison.price_options")}
                </Text>
            </div>
            <div className={styles.mobileScrollArea}>
                <div className={styles.mobileCardsWrapper}>
                    <RadioCardGroup stacked={false} gap="xxs">
                        {buyOptions.map((buyOption, index) => {
                            const stickerType = getBuyOptionStickerType(buyOption.label);
                            return (
                                <BuyOptionCard
                                    key={`price-comparison-buy-option-${buyOption.link_id}`}
                                    variant={"radio"}
                                    id={buyOption.link_id.toString()}
                                    name="price-compare-items"
                                    value={buyOption.link_id.toString()}
                                    checked={selectedBuyOption.link_id === buyOption.link_id}
                                    buyOption={buyOption}
                                    buyOptionIndex={index}
                                    onChange={() => onItemSelect(buyOption)}
                                    shippingInformation={getShippingInformation(buyOption)}
                                    promotion={getPromotion(product, buyOption)}
                                    stickerType={stickerType || null}
                                    comparePricesCount={buyOptions.length}
                                    retailerLogoURL={buyOption.retailer_image}
                                    onBuyButtonClick={() => onBuyButtonClick(buyOption, index)}
                                    onRetailerClick={() => onRetailerClick(buyOption, index)}
                                />
                            );
                        })}
                    </RadioCardGroup>
                </div>
            </div>
            <div className={styles.buttonsWrapper}>
                <Button title={gettext("general.select")} onClick={selectBuyOption} width="full" />
                {isCheckout && (
                    <div data-testid="price-comparison-cards-view-seller-details">
                        <Button
                            title={gettext("product.view_seller_details")}
                            onClick={() => {}}
                            width="full"
                            variant="secondary"
                        />
                    </div>
                )}
            </div>
        </form>
    );
}

function PriceComparisonCardsDesktop({
    product,
    buyOptions,
    onBuyButtonClick,
    onRetailerClick,
}: PriceComparisonCardsDeviceProps): JSX.Element {
    const { productPurchaseType } = useInStockProductPageContext();
    const isCheckout = productPurchaseType === ProductPurchaseType.CHECKOUT;

    // If more then 5 buyOptions we only show the first 5
    const displayedBuyOptionsLimit = 5;
    const [displayedBuyOptions, setDisplayedBuyOptions] = useState(
        buyOptions.slice(0, displayedBuyOptionsLimit)
    );

    // count of the not displayed buyOptions
    const [notDisplayedBuyOptionsCount, setNotDisplayedBuyOptionsCount] = useState(
        buyOptions.length - displayedBuyOptionsLimit
    );

    useEffect(() => {
        // Ignore if the buy options did not change
        if (
            JSON.stringify(buyOptions.map((buyOption) => buyOption.link_id)) ===
            JSON.stringify(displayedBuyOptions.map((buyOption) => buyOption.link_id))
        ) {
            return;
        }

        setDisplayedBuyOptions(buyOptions.slice(0, displayedBuyOptionsLimit));
        setNotDisplayedBuyOptionsCount(buyOptions.length - displayedBuyOptionsLimit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyOptions]);

    function onClickSeeMorePrices(): void {
        setDisplayedBuyOptions(buyOptions);
        analytics.event("see-more-prices", "clicked", "pdp");
    }

    const seeMorePriceSingular = gettext(
        "in_stock_product.compare_prices.see_more_prices_cta_singular"
    );
    const seeMorePricePlural = gettext("in_stock_product.compare_prices.see_more_prices_cta");

    return (
        <div className={styles.desktopWrapper}>
            <Text as="p" textStyle={"body-s"}>
                {gettext("price_comparison.price_options")}
            </Text>
            <div className={styles.desktopCards}>
                {displayedBuyOptions.map((buyOption, index) => {
                    const stickerType = getBuyOptionStickerType(buyOption.label);

                    return (
                        <BuyOptionCard
                            key={`price-comparison-buy-option-${buyOption.link_id}`}
                            variant={"wide"}
                            buyOption={buyOption}
                            buyOptionIndex={index}
                            shippingInformation={getShippingInformation(buyOption)}
                            promotion={getPromotion(product, buyOption)}
                            stickerType={stickerType || null}
                            comparePricesCount={displayedBuyOptions.length}
                            retailerLogoURL={buyOption.retailer_image}
                            onBuyButtonClick={() => onBuyButtonClick(buyOption, index)}
                            onRetailerClick={() => onRetailerClick(buyOption, index)}
                            isCheckout={isCheckout}
                        />
                    );
                })}
            </div>
            {displayedBuyOptions.length < buyOptions.length && (
                <div className={styles.seeMoreButton} onClick={onClickSeeMorePrices}>
                    <Text textStyle="body-s" underline>
                        {ngettext(
                            seeMorePriceSingular,
                            seeMorePricePlural,
                            notDisplayedBuyOptionsCount,
                            {
                                count: notDisplayedBuyOptionsCount,
                            }
                        )}
                    </Text>
                </div>
            )}
        </div>
    );
}

export function PriceComparisonCards({
    product,
    buyOptions,
}: PriceComparisonCardsProps): JSX.Element {
    const { isMobileViewport } = useDomViewport();

    const onBuyButtonClick = useCallback(
        (buyOption: ProductBuyOptionsSerializer, index: number) => {
            analytics.event("buy_area", "clicked", "price_comparison", false, {
                buyOptionId: buyOption.link_id.toString(),
                buyOptionIndex: index,
            });
        },
        []
    );

    const onRetailerClick = useCallback((buyOption: ProductBuyOptionsSerializer, index: number) => {
        analytics.event("retailer_store", "clicked", "price_comparison", false, {
            retailerNetwork: buyOption.retailer_network,
            buyOptionId: buyOption.link_id.toString(),
            buyOptionIndex: index,
        });
    }, []);

    return isMobileViewport ? (
        <PriceComparisonCardsMobile
            product={product}
            buyOptions={buyOptions}
            onBuyButtonClick={onBuyButtonClick}
            onRetailerClick={onRetailerClick}
        />
    ) : (
        <PriceComparisonCardsDesktop
            product={product}
            buyOptions={buyOptions}
            onBuyButtonClick={onBuyButtonClick}
            onRetailerClick={onRetailerClick}
        />
    );
}
